import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  AddRounded,
  InterpreterModeRounded,
  TranslateRounded,
  PeopleRounded,
  RecordVoiceOverRounded,
  PointOfSaleRounded,
  RequestPageRounded,
  MoreHorizRounded,
  FormatListBulletedRounded,
  AccountCircle,
  Logout,
  CloseRounded,
  MarkEmailReadRounded,
  ChevronLeftRounded,
  NotificationsNoneRounded,
  MoreVertRounded,
  SearchRounded,
  PlayArrowRounded,
  StopRounded,
  DashboardRounded,
} from "@mui/icons-material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import FlexBetween from "components/FlexBetween";
import { useDispatch } from "react-redux";
import { setMode } from "state";
import {
  Typography,
  AppBar,
  Card,
  IconButton,
  Stack,
  Toolbar,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Popover,
  Button,
  Divider,
  Backdrop,
  Avatar,
  Box,
  Badge,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { doSignOut } from "../firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";
import { usePagePresence } from "hooks/useAssignmentPresence";
import PresenceIndicator from "./PresenceIndicator";
import GlobalSearchBar from "./GlobalSearchBar";
import { getAvatarColor } from "helpers/avatarHelpers";
import api from "services/api";
import { NAVBAR_HEIGHT } from "constants/layout";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useWebSocket } from "contexts/WebSocketContext";
import dayjs from "dayjs";

const NOTIFICATION_SOUND_STORAGE_KEY = "ce_portal.notificationSoundPreference";
const AUTO_NOTE_PATTERN = "Portal Automatic Note:";
const getAutoNoteAcknowledgedKey = (userId) => `ce_portal.autoNoteAcknowledged.${userId || "anonymous"}`;

const parseAutoNoteSuggestedTime = (notes) => {
  if (!notes) return null;
  const match = notes.match(/Possible Clock out time:\s*(\d{1,2}:\d{2})/i);
  return match ? match[1] : null;
};
const NOTIFICATION_SYNC_INTERVAL_MS = 30000;
const CLOCK_REMINDER_DELAY_MS = 15 * 60 * 1000;
const CLOCK_REMINDER_SNOOZE_MS = 30 * 60 * 1000;
const CLOCK_REMINDER_ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const CLOCK_REMINDER_DAILY_ESCALATION_CAP = 2;

const getTodayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getClockReminderStorageKey = (userId) => `ce_portal.clockReminderState.${userId || "anonymous"}`;

const getNotificationGroupKey = (notification) => {
  const candidate = notification?.VisibleAt || notification?.createdAt;
  if (!candidate) return "unknown";

  const parsed = new Date(candidate);
  if (Number.isNaN(parsed.getTime())) return String(candidate);

  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")} ${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;
};

const parseReadFlag = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }
  return false;
};

const normalizeNotification = (notification) => ({
  ...notification,
  IsRead: parseReadFlag(notification?.IsRead),
});

const getNotificationTimestamp = (notification) => {
  const candidate = notification?.VisibleAt || notification?.createdAt;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const sortNotificationsLatestFirst = (items) =>
  [...items].sort((a, b) => {
    const readBucketDifference = Number(parseReadFlag(a?.IsRead)) - Number(parseReadFlag(b?.IsRead));
    if (readBucketDifference !== 0) {
      return readBucketDifference;
    }

    return getNotificationTimestamp(b) - getNotificationTimestamp(a);
  });

// Reminder Watch notifications carry the target date in their metadata.
// Falls back to tomorrow when the payload has no usable date.
const getReminderWatchTargetDate = (notification) => {
  const metadata = notification?.Metadata || {};
  const rawDate =
    metadata?.targetDate || metadata?.date || metadata?.reminderDate;
  if (rawDate) {
    const parsed = dayjs(rawDate);
    if (parsed.isValid()) return parsed;
  }
  return dayjs().add(1, "day");
};

const Navbar = ({ user }) => {
  const dispatch = useDispatch();
  const { viewers } = usePagePresence();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useWebSocket();
  const {
    setOverlayData,
    setOverlayName,
    customPageTitle,
    activeMenu,
    setActiveMenu,
    isCollapsed: contextIsCollapsed,
    setIsCollapsed: setContextIsCollapsed,
    showSnackbar,
  } = useStateContext();

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const notificationPopoverOpen = Boolean(notificationAnchorEl);
  const [quickCreateAnchorEl, setQuickCreateAnchorEl] = useState(null);
  const quickCreateOpen = Boolean(quickCreateAnchorEl);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const [notifications, setNotifications] = useState([]);
  const [floatingNotifications, setFloatingNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const isMountedRef = useRef(true);
  const knownNotificationIdsRef = useRef(new Set());
  const knownNotificationReadStateRef = useRef(new Map());
  const hasInitializedNotificationSyncRef = useRef(false);
  const notificationsLastFetchedAtRef = useRef(0);
  const floatingNotificationTimersRef = useRef(new Map());
  const [notificationSound, setNotificationSound] = useState(() => {
    try {
      const cached = window.localStorage.getItem(NOTIFICATION_SOUND_STORAGE_KEY);
      return cached
        ? JSON.parse(cached)
        : {
            enabled: true,
            soundKey: "",
            soundUrl: "",
          };
    } catch (error) {
      return {
        enabled: true,
        soundKey: "",
        soundUrl: "",
      };
    }
  });
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [activeTimesheetEntry, setActiveTimesheetEntry] = useState(null);
  const [isTimesheetStatusLoading, setIsTimesheetStatusLoading] = useState(false);
  const [isClockActionLoading, setIsClockActionLoading] = useState(false);
  const [showClockReminderBanner, setShowClockReminderBanner] = useState(false);
  const [clockReminderEscalationsToday, setClockReminderEscalationsToday] = useState(0);
  const [clockReminderSnoozedUntil, setClockReminderSnoozedUntil] = useState(0);
  const [clockReminderDismissedForToday, setClockReminderDismissedForToday] = useState(false);
  const [clockReminderOptedInMoreToday, setClockReminderOptedInMoreToday] = useState(false);
  const [clockReminderWindowStartedAt, setClockReminderWindowStartedAt] = useState(null);
  const [lastPortalActivityAt, setLastPortalActivityAt] = useState(() => Date.now());
  const [clockReminderNotificationSentForWindow, setClockReminderNotificationSentForWindow] = useState(false);
  const [pendingAutoNoteEntry, setPendingAutoNoteEntry] = useState(null);
  const autoNoteAcknowledgedRef = useRef(new Set());

  const isVisibleNow = (notification) => {
    if (!notification?.VisibleAt) return true;
    const visibleAtDate = new Date(notification.VisibleAt);
    if (Number.isNaN(visibleAtDate.getTime())) return true;
    return visibleAtDate.getTime() <= Date.now();
  };

  const isAdmin = user?.Role === "Admin" || user?.role === "Admin";
  const isTimesheetsHiddenForCurrentUser = Number(user?.id) === 1;
  const showClockPresenceStyle = !isTimesheetsHiddenForCurrentUser;

  const fetchTimesheetStatus = useCallback(async ({ silent = true } = {}) => {
    if (!user?.id) {
      setIsClockedIn(false);
      setActiveTimesheetEntry(null);
      return;
    }

    if (!silent) {
      setIsTimesheetStatusLoading(true);
    }

    try {
      const response = await api.get("/timesheets/me/status");
      const entry = response?.data?.activeEntry || null;
      setIsClockedIn(Boolean(response?.data?.isClockedIn));
      setActiveTimesheetEntry(entry);

      if (
        entry &&
        !entry.ClockOutAt &&
        entry.Notes?.includes(AUTO_NOTE_PATTERN) &&
        !autoNoteAcknowledgedRef.current.has(String(entry.id))
      ) {
        setPendingAutoNoteEntry(entry);
      }
    } catch (error) {
      // Keep current UI state when status refresh fails.
    } finally {
      if (!silent) {
        setIsTimesheetStatusLoading(false);
      }
    }
  }, [user?.id]);

  const handleClockIn = useCallback(async () => {
    if (isClockActionLoading || isClockedIn) return;

    setIsClockActionLoading(true);
    try {
      const requestedHourlyRate = Number(user?.HourlyRate ?? user?.hourlyRate ?? 0);
      const payload = Number.isFinite(requestedHourlyRate) && requestedHourlyRate > 0
        ? { hourlyRateSnapshot: requestedHourlyRate }
        : {};

      await api.post("/timesheets/clock-in", payload);
      await fetchTimesheetStatus({ silent: false });
      showSnackbar("Clocked in successfully", theme, "success");
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Failed to clock in",
        theme,
        "error",
      );
    } finally {
      setIsClockActionLoading(false);
    }
  }, [fetchTimesheetStatus, isClockActionLoading, isClockedIn, showSnackbar, theme, user?.HourlyRate, user?.hourlyRate]);

  const handleClockOut = useCallback(async () => {
    if (isClockActionLoading || !isClockedIn) return;

    setIsClockActionLoading(true);
    try {
      const requestedHourlyRate = Number(user?.HourlyRate ?? user?.hourlyRate ?? 0);
      const payload = Number.isFinite(requestedHourlyRate) && requestedHourlyRate > 0
        ? { hourlyRateSnapshot: requestedHourlyRate }
        : {};

      await api.post("/timesheets/clock-out", payload);
      await fetchTimesheetStatus({ silent: false });
      showSnackbar("Clocked out successfully", theme, "success");
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Failed to clock out",
        theme,
        "error",
      );
    } finally {
      setIsClockActionLoading(false);
    }
  }, [fetchTimesheetStatus, isClockActionLoading, isClockedIn, showSnackbar, theme, user?.HourlyRate, user?.hourlyRate]);

  const presenceGlowColor = isClockedIn
    ? theme.palette.primary.main
    : theme.palette.error.main;
  const lastActivityStateCommitRef = useRef(0);

  useEffect(() => {
    const storageKey = getClockReminderStorageKey(user?.id);
    const todayKey = getTodayKey();

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setClockReminderEscalationsToday(0);
        setClockReminderSnoozedUntil(0);
        setClockReminderDismissedForToday(false);
        setClockReminderOptedInMoreToday(false);
        return;
      }

      const parsed = JSON.parse(raw);
      const sameDay = parsed?.date === todayKey;

      if (!sameDay) {
        setClockReminderEscalationsToday(0);
        setClockReminderSnoozedUntil(0);
        setClockReminderDismissedForToday(false);
        setClockReminderOptedInMoreToday(false);
        return;
      }

      setClockReminderEscalationsToday(Number(parsed?.escalationCount || 0));
      setClockReminderSnoozedUntil(Number(parsed?.snoozedUntil || 0));
      setClockReminderDismissedForToday(Boolean(parsed?.dismissedForToday));
      setClockReminderOptedInMoreToday(Boolean(parsed?.optedInMoreToday));
    } catch (error) {
      setClockReminderEscalationsToday(0);
      setClockReminderSnoozedUntil(0);
      setClockReminderDismissedForToday(false);
      setClockReminderOptedInMoreToday(false);
    }
  }, [user?.id]);

  useEffect(() => {
    const storageKey = getClockReminderStorageKey(user?.id);
    const payload = {
      date: getTodayKey(),
      escalationCount: clockReminderEscalationsToday,
      snoozedUntil: clockReminderSnoozedUntil,
      dismissedForToday: clockReminderDismissedForToday,
      optedInMoreToday: clockReminderOptedInMoreToday,
    };

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
      // Ignore storage errors.
    }
  }, [
    user?.id,
    clockReminderEscalationsToday,
    clockReminderSnoozedUntil,
    clockReminderDismissedForToday,
    clockReminderOptedInMoreToday,
  ]);

  useEffect(() => {
    const markActivity = () => {
      const nowTs = Date.now();
      // Throttle state updates from high-frequency activity events.
      if (nowTs - lastActivityStateCommitRef.current < 10000) {
        return;
      }
      lastActivityStateCommitRef.current = nowTs;
      setLastPortalActivityAt(nowTs);
    };

    const eventNames = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    eventNames.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));

    return () => {
      eventNames.forEach((eventName) => window.removeEventListener(eventName, markActivity));
    };
  }, []);

  useEffect(() => {
    if (isClockedIn) {
      setShowClockReminderBanner(false);
      setClockReminderWindowStartedAt(null);
      setClockReminderNotificationSentForWindow(false);
      return;
    }

    setClockReminderWindowStartedAt((prev) => prev ?? Date.now());
  }, [isClockedIn]);

  useEffect(() => {
    const evaluateReminderEscalation = () => {
      const now = Date.now();

      if (isClockedIn) {
        setShowClockReminderBanner(false);
        return;
      }

      if (!clockReminderWindowStartedAt) {
        return;
      }

      if (clockReminderDismissedForToday) {
        setShowClockReminderBanner(false);
        return;
      }

      if (clockReminderSnoozedUntil && now < clockReminderSnoozedUntil) {
        setShowClockReminderBanner(false);
        return;
      }

      if (!clockReminderOptedInMoreToday && clockReminderEscalationsToday >= CLOCK_REMINDER_DAILY_ESCALATION_CAP) {
        setShowClockReminderBanner(false);
        return;
      }

      if (now - clockReminderWindowStartedAt < CLOCK_REMINDER_DELAY_MS) {
        setShowClockReminderBanner(false);
        return;
      }

      const isUserActive = now - lastPortalActivityAt <= CLOCK_REMINDER_ACTIVE_WINDOW_MS;
      if (!isUserActive) {
        setShowClockReminderBanner(false);
        return;
      }

      const shouldEscalateByBanner = document.visibilityState === "visible" && document.hasFocus();

      if (shouldEscalateByBanner) {
        setShowClockReminderBanner((prev) => {
          if (prev) return prev;
          setClockReminderEscalationsToday((count) => count + 1);
          return true;
        });
        return;
      }

      if (!clockReminderNotificationSentForWindow && "Notification" in window) {
        const maybeSend = async () => {
          try {
            if (Notification.permission === "default") {
              await Notification.requestPermission();
            }

            if (Notification.permission !== "granted") {
              return;
            }

            const desktopNotification = new Notification("Not Clocked In", {
              body: "You are using Portal but still clocked out",
              tag: `ce-clock-reminder-${getTodayKey()}`,
              renotify: true,
            });

            desktopNotification.onclick = () => {
              window.focus();
              navigate("/settings?tab=1");
              desktopNotification.close();
            };

            window.setTimeout(() => {
              desktopNotification.close();
            }, 12000);

            setClockReminderNotificationSentForWindow(true);
            setClockReminderEscalationsToday((prev) => prev + 1);
          } catch (error) {
            // Ignore notification errors.
          }
        };

        void maybeSend();
      }
    };

    evaluateReminderEscalation();
    const intervalId = window.setInterval(evaluateReminderEscalation, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    isClockedIn,
    clockReminderWindowStartedAt,
    clockReminderDismissedForToday,
    clockReminderSnoozedUntil,
    clockReminderEscalationsToday,
    clockReminderOptedInMoreToday,
    lastPortalActivityAt,
    clockReminderNotificationSentForWindow,
    navigate,
  ]);

  // Load persisted acknowledged auto-note IDs from localStorage.
  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = window.localStorage.getItem(getAutoNoteAcknowledgedKey(user.id));
      if (raw) {
        const ids = JSON.parse(raw);
        autoNoteAcknowledgedRef.current = new Set(Array.isArray(ids) ? ids.map(String) : []);
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [user?.id]);

  const handleAutoNoteAcknowledge = useCallback((entry) => {
    if (!entry?.id || !user?.id) return;
    const idStr = String(entry.id);
    autoNoteAcknowledgedRef.current.add(idStr);
    try {
      window.localStorage.setItem(
        getAutoNoteAcknowledgedKey(user.id),
        JSON.stringify([...autoNoteAcknowledgedRef.current]),
      );
    } catch (e) {
      // ignore
    }
    setPendingAutoNoteEntry(null);
  }, [user?.id]);

  const handleAutoNoteDismiss = useCallback(() => {
    handleAutoNoteAcknowledge(pendingAutoNoteEntry);
  }, [handleAutoNoteAcknowledge, pendingAutoNoteEntry]);

  const handleAutoNoteReview = useCallback(() => {
    if (!pendingAutoNoteEntry) return;
    handleAutoNoteAcknowledge(pendingAutoNoteEntry);
    setOverlayData({ timesheet: pendingAutoNoteEntry });
    setOverlayName(OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW);
  }, [handleAutoNoteAcknowledge, pendingAutoNoteEntry, setOverlayData, setOverlayName]);

  const handleClockReminderClockInNow = useCallback(async () => {
    await handleClockIn();
    setShowClockReminderBanner(false);
  }, [handleClockIn]);

  const handleClockReminderSnooze = useCallback(() => {
    const snoozedUntil = Date.now() + CLOCK_REMINDER_SNOOZE_MS;
    setClockReminderSnoozedUntil(snoozedUntil);
    setClockReminderOptedInMoreToday(true);
    setShowClockReminderBanner(false);
  }, []);

  const handleClockReminderDismissToday = useCallback(() => {
    setClockReminderDismissedForToday(true);
    setShowClockReminderBanner(false);
  }, []);

  const handleNotificationsClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    requestBrowserNotificationPermission();

    const isStale = Date.now() - notificationsLastFetchedAtRef.current > 45000;
    const shouldRefresh = (notifications.length === 0 || isStale) && !loadingNotifications;

    if (shouldRefresh) {
      fetchNotifications({ silent: true });
    }
  };

  const handleNotificationsClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleQuickCreateClick = (event) => {
    setQuickCreateAnchorEl(event.currentTarget);
  };

  const handleQuickCreateClose = () => {
    setQuickCreateAnchorEl(null);
  };

  const handleQuickCreateTaskClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: null,
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading task form...",
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);

    try {
      const [membersRes, clientsRes, linguistsRes, servicesRes, invoicesRes] =
        await Promise.all([
          api.get("/members"),
          api.get("/clients/tasks"),
          api.get("/linguists/tasks"),
          api.get("/services/tasks"),
          api.get("/invoices"),
        ]);

      setOverlayData({
        Action: OVERLAY_NAMES.TASK_ACTION_NEW,
        Task: null,
        prefetchedTaskFormData: {
          members: membersRes.data,
          clients: clientsRes.data,
          linguists: linguistsRes.data,
          services: servicesRes.data,
          invoices: invoicesRes.data,
        },
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        Action: OVERLAY_NAMES.TASK_ACTION_NEW,
        Task: null,
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateClientClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    setOverlayData({
      ClientTypes: [],
      ContractStatuses: [],
      ClientServiceTypes: [],
      PaymentMethods: [],
      Action: OVERLAY_NAMES.CLIENT_ACTION_NEW,
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading client form...",
    });
    setOverlayName(OVERLAY_NAMES.CLIENT_NEW_EDIT);

    try {
      const [
        clientTypesRes,
        contractStatusesRes,
        clientServiceTypesRes,
        paymentMethodsRes,
      ] = await Promise.all([
        api.get("/clients/clientTypes"),
        api.get("/clients/contractStatuses"),
        api.get("/clients/clientServiceTypes"),
        api.get("/clients/paymentMethods"),
      ]);

      setOverlayData({
        ClientTypes: clientTypesRes.data,
        ContractStatuses: contractStatusesRes.data,
        ClientServiceTypes: clientServiceTypesRes.data,
        PaymentMethods: paymentMethodsRes.data,
        Action: OVERLAY_NAMES.CLIENT_ACTION_NEW,
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        ClientTypes: [],
        ContractStatuses: [],
        ClientServiceTypes: [],
        PaymentMethods: [],
        Action: OVERLAY_NAMES.CLIENT_ACTION_NEW,
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateLinguistClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    setOverlayData({
      Languages: [],
      LinguistStatuses: [],
      LanguageModes: [],
      Action: OVERLAY_NAMES.LINGUIST_ACTION_NEW,
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading linguist form...",
    });
    setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);

    try {
      const [languagesRes, linguistStatusesRes, languageModesRes] =
        await Promise.all([
          api.get("/languages"),
          api.get("/linguists/linguistStatuses"),
          api.get("/linguists/languageModes"),
        ]);

      setOverlayData({
        Languages: languagesRes.data,
        LinguistStatuses: linguistStatusesRes.data,
        LanguageModes: languageModesRes.data,
        Action: OVERLAY_NAMES.LINGUIST_ACTION_NEW,
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        Languages: [],
        LinguistStatuses: [],
        LanguageModes: [],
        Action: OVERLAY_NAMES.LINGUIST_ACTION_NEW,
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateInvoiceClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    setOverlayData({
      Clients: [],
      ClientsOPIMinimums: [],
      Linguists: [],
      Languages: [],
      InterpretationStatuses: [],
      InterpretationTypes: [],
      Action: OVERLAY_NAMES.INVOICES_ACTION_NEW,
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading invoice form...",
    });
    setOverlayName(OVERLAY_NAMES.INVOICES_NEW_EDIT);

    try {
      const [
        clientsRes,
        clientsOPIMinimumsRes,
        linguistsRes,
        languagesRes,
        interpretationStatusesRes,
        interpretationTypesRes,
      ] = await Promise.all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
      ]);

      const filteredLanguages = Array.isArray(languagesRes.data)
        ? languagesRes.data.filter((language) => language.Name !== "ALL")
        : [];

      setOverlayData({
        Clients: clientsRes.data,
        ClientsOPIMinimums: clientsOPIMinimumsRes.data,
        Linguists: linguistsRes.data,
        Languages: filteredLanguages,
        InterpretationStatuses: interpretationStatusesRes.data,
        InterpretationTypes: Array.isArray(interpretationTypesRes.data)
          ? interpretationTypesRes.data.slice(0, 5)
          : [],
        Action: OVERLAY_NAMES.INVOICES_ACTION_NEW,
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        Clients: [],
        ClientsOPIMinimums: [],
        Linguists: [],
        Languages: [],
        InterpretationStatuses: [],
        InterpretationTypes: [],
        Action: OVERLAY_NAMES.INVOICES_ACTION_NEW,
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateExpenseClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    setOverlayData({
      selectedYear: String(new Date().getFullYear()),
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading expense form...",
    });

    setOverlayName(OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT);

    try {
      const categoriesRes = await api.get("/expenses/categories");
      setOverlayData({
        selectedYear: String(new Date().getFullYear()),
        Categories: categoriesRes.data || [],
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        selectedYear: String(new Date().getFullYear()),
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateInterpretationClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "short" });
    const year = String(now.getFullYear());

    setOverlayData({
      Clients: [],
      ClientsOPIMinimums: [],
      Linguists: [],
      Languages: [],
      Interpretations: [],
      InterpretationStatuses: [],
      InterpretationTypes: [],
      Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
      CreationSource: "quick_create",
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading interpretation form...",
    });
    setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

    try {
      const [
        clientsRes,
        clientsOPIMinimumsRes,
        linguistsRes,
        languagesRes,
        interpretationStatusesRes,
        interpretationTypesRes,
        interpretationsRes,
      ] = await Promise.all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
        api.get("/services/interpretations", {
          params: { months: month, year },
        }),
      ]);

      setOverlayData({
        Clients: clientsRes.data,
        ClientsOPIMinimums: clientsOPIMinimumsRes.data,
        Linguists: linguistsRes.data,
        Languages: languagesRes.data,
        Interpretations: interpretationsRes.data,
        InterpretationStatuses: interpretationStatusesRes.data,
        InterpretationTypes: interpretationTypesRes.data,
        Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
        CreationSource: "quick_create",
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        Clients: [],
        ClientsOPIMinimums: [],
        Linguists: [],
        Languages: [],
        Interpretations: [],
        InterpretationStatuses: [],
        InterpretationTypes: [],
        Action: OVERLAY_NAMES.INTERPRETATION_ACTION_NEW,
        CreationSource: "quick_create",
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateTranslationClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "short" });
    const year = String(now.getFullYear());

    setOverlayData({
      Clients: [],
      Linguists: [],
      Languages: [],
      Translations: [],
      TranslationStatuses: [],
      TranslationTypes: [],
      ProjectNumber: 1,
      RateTypes: [],
      Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
      CreationSource: "quick_create",
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading translation form...",
    });
    setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);

    try {
      const [
        clientsRes,
        linguistsRes,
        languagesRes,
        translationStatusesRes,
        translationTypesRes,
        latestProjectNumberRes,
        rateTypesRes,
        translationsRes,
      ] = await Promise.all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/translations/statuses"),
        api.get("/services/translations/types"),
        api.get("/services/translations/latestProjectNumber"),
        api.get("/services/translations/rateTypes"),
        api.get("/services/translations", {
          params: { months: month, year },
        }),
      ]);

      const filteredLanguages = Array.isArray(languagesRes.data)
        ? languagesRes.data.filter((language) => language.Name !== "ALL")
        : [];

      setOverlayData({
        Clients: clientsRes.data,
        Linguists: linguistsRes.data,
        Languages: filteredLanguages,
        Translations: translationsRes.data,
        TranslationStatuses: translationStatusesRes.data,
        TranslationTypes: Array.isArray(translationTypesRes.data)
          ? translationTypesRes.data.slice(0, 5)
          : [],
        ProjectNumber:
          Number(latestProjectNumberRes?.data?.ProjectNumber || 0) + 1,
        RateTypes: rateTypesRes.data,
        Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
        CreationSource: "quick_create",
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        Clients: [],
        Linguists: [],
        Languages: [],
        Translations: [],
        TranslationStatuses: [],
        TranslationTypes: [],
        ProjectNumber: 1,
        RateTypes: [],
        Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
        CreationSource: "quick_create",
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateOtherServiceClick = async () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);

    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "short" });
    const year = String(now.getFullYear());

    setOverlayData({
      Clients: [],
      Linguists: [],
      Languages: [],
      OtherServices: [],
      OtherServiceStatuses: [],
      OtherServiceTypes: [],
      ProjectNumber: 1,
      RateTypes: 0,
      Action: OVERLAY_NAMES.OTHERSERVICE_ACTION_NEW,
      CreationSource: "quick_create",
      quickCreateLoading: true,
      quickCreateLoadingLabel: "Loading other service form...",
    });
    setOverlayName(OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT);

    try {
      const [
        clientsRes,
        linguistsRes,
        languagesRes,
        otherServiceStatusesRes,
        otherServiceTypesRes,
        latestProjectNumberRes,
        otherServicesRes,
      ] = await Promise.all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/otherServices/statuses"),
        api.get("/services/otherServices/types"),
        api.get("/services/otherServices/latestProjectNumber"),
        api.get("/services/otherServices", {
          params: { months: month, year },
        }),
      ]);

      const filteredLanguages = Array.isArray(languagesRes.data)
        ? languagesRes.data.filter((language) => language.Name !== "ALL")
        : [];

      setOverlayData({
        Clients: clientsRes.data,
        Linguists: linguistsRes.data,
        Languages: filteredLanguages,
        OtherServices: otherServicesRes.data,
        OtherServiceStatuses: otherServiceStatusesRes.data,
        OtherServiceTypes: otherServiceTypesRes.data,
        ProjectNumber:
          Number(latestProjectNumberRes?.data?.ProjectNumber || 0) + 1,
        RateTypes: 0,
        Action: OVERLAY_NAMES.OTHERSERVICE_ACTION_NEW,
        CreationSource: "quick_create",
        quickCreateLoading: false,
      });
    } catch (error) {
      setOverlayData({
        Clients: [],
        Linguists: [],
        Languages: [],
        OtherServices: [],
        OtherServiceStatuses: [],
        OtherServiceTypes: [],
        ProjectNumber: 1,
        RateTypes: 0,
        Action: OVERLAY_NAMES.OTHERSERVICE_ACTION_NEW,
        CreationSource: "quick_create",
        quickCreateLoading: false,
      });
    }
  };

  const handleQuickCreateOverlayShowcaseClick = () => {
    setQuickCreateAnchorEl(null);
    setMobileMenuAnchorEl(null);
    setOverlayName(OVERLAY_NAMES.OVERLAY_SHOWCASE);
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const playNotificationSound = useCallback(() => {
    if (!notificationSound.enabled || !notificationSound.soundUrl) return;

    try {
      const audio = new Audio(notificationSound.soundUrl);
      audio.volume = 1;
      audio.play().catch(() => {});
    } catch (error) {}
  }, [notificationSound.enabled, notificationSound.soundUrl]);

  const requestBrowserNotificationPermission = useCallback(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "default") return;

    Notification.requestPermission().catch(() => {});
  }, []);

  const dismissFloatingNotification = useCallback((notificationId) => {
    const existingTimer = floatingNotificationTimersRef.current.get(notificationId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      floatingNotificationTimersRef.current.delete(notificationId);
    }

    setFloatingNotifications((prev) => prev.filter((item) => item.id !== notificationId));
  }, []);

  const dismissFloatingNotificationGroup = useCallback((groupItems) => {
    const ids = groupItems.map((item) => item.id);

    ids.forEach((notificationId) => {
      const existingTimer = floatingNotificationTimersRef.current.get(notificationId);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
        floatingNotificationTimersRef.current.delete(notificationId);
      }
    });

    setFloatingNotifications((prev) =>
      prev.filter((item) => !ids.includes(item.id))
    );
  }, []);

  const showFloatingNotification = useCallback((notification) => {
    if (!notification?.id) return;

    setFloatingNotifications((prev) => {
      if (prev.some((item) => item.id === notification.id)) {
        return prev;
      }

      return [notification, ...prev].slice(0, 3);
    });

    const existingTimer = floatingNotificationTimersRef.current.get(notification.id);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const timeoutId = window.setTimeout(() => {
      floatingNotificationTimersRef.current.delete(notification.id);
      setFloatingNotifications((prev) => prev.filter((item) => item.id !== notification.id));
    }, 30000);

    floatingNotificationTimersRef.current.set(notification.id, timeoutId);
  }, []);

  const fetchNotifications = useCallback(async ({ playSoundForNew = false, silent = false } = {}) => {
    if (!user?.id) return;
    if (!silent) {
      setLoadingNotifications(true);
    }

    try {
      const countResponse = await api.get("/notifications/count");
      const listResponse = await api.get("/notifications?limit=20");
      const nextNotifications = Array.isArray(listResponse.data)
        ? listResponse.data.map(normalizeNotification)
        : [];
      const sortedNotifications = sortNotificationsLatestFirst(nextNotifications);
      const knownIds = knownNotificationIdsRef.current;
      const knownReadStates = knownNotificationReadStateRef.current;

      if (!hasInitializedNotificationSyncRef.current) {
        sortedNotifications.forEach((item) => {
          knownIds.add(item.id);
          knownReadStates.set(item.id, parseReadFlag(item.IsRead));
        });
        hasInitializedNotificationSyncRef.current = true;
      } else {
        const newlyDiscoveredUnread = sortedNotifications.filter(
          (item) => {
            if (parseReadFlag(item.IsRead)) return false;

            const isNewId = !knownIds.has(item.id);
            const wasReadBefore = knownReadStates.get(item.id) === true;

            return isNewId || wasReadBefore;
          }
        );

        sortedNotifications.forEach((item) => {
          knownIds.add(item.id);
          knownReadStates.set(item.id, parseReadFlag(item.IsRead));
        });

        if (playSoundForNew) {
          const now = Date.now();
          const freshNotifications = newlyDiscoveredUnread.filter((item) => {
            const timestamp = getNotificationTimestamp(item);
            return timestamp > 0 && now >= timestamp && now - timestamp <= 3600000;
          });

          freshNotifications.forEach((item) => {
            showFloatingNotification(item);

            const isBackground =
              document.visibilityState !== "visible" || !document.hasFocus();

            if (isBackground && "Notification" in window && Notification.permission === "granted") {
              try {
                const title = item?.Title || "Notification";
                const message = item?.Message || "";
                const desktopNotification = new Notification(title, {
                  body: message,
                  tag: `ce-notification-${item?.id || Date.now()}`,
                  renotify: true,
                });

                desktopNotification.onclick = () => {
                  window.focus();
                  navigate("/notifications");
                  desktopNotification.close();
                };

                window.setTimeout(() => {
                  desktopNotification.close();
                }, 12000);
              } catch (error) {}
            }
          });

          if (freshNotifications.length > 0) {
            playNotificationSound();
          }
        }
      }

      setNotifications(sortedNotifications);
      setUnreadCount(Number(countResponse?.data?.unreadCount || 0));
      notificationsLastFetchedAtRef.current = Date.now();
    } catch (error) {
      setNotifications([]);
    } finally {
      if (!silent) {
        setLoadingNotifications(false);
      }
    }
  }, [user?.id, playNotificationSound, showFloatingNotification, navigate]);

  const fetchNotificationSoundPreference = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await api.get("/notificationSettings/member/preferences");
      const preference = response.data?.preference || null;
      const soundOptions = Array.isArray(response.data?.soundOptions) ? response.data.soundOptions : [];
      const selectedSound = soundOptions.find((option) => option.key === preference?.SoundPreset) || soundOptions[0] || null;

      const nextSoundState = {
        enabled: preference?.IsSoundEnabled !== false,
        soundKey: selectedSound?.key || "",
        soundUrl: selectedSound?.url || "",
      };

      setNotificationSound(nextSoundState);
      window.localStorage.setItem(NOTIFICATION_SOUND_STORAGE_KEY, JSON.stringify(nextSoundState));
    } catch (error) {}
  }, [user?.id]);

  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      knownNotificationReadStateRef.current.set(notificationId, true);
      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => (item.id === notificationId ? { ...item, IsRead: true, ReadAt: new Date().toISOString() } : item))
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      fetchNotifications({ silent: true });
    }
  }, [fetchNotifications]);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await api.patch("/notifications/read-all");
      notifications.forEach((item) => {
        knownNotificationReadStateRef.current.set(item.id, true);
      });
      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => ({ ...item, IsRead: true, ReadAt: item.ReadAt || new Date().toISOString() }))
        )
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      fetchNotifications({ silent: true });
    }
  }, [fetchNotifications, notifications]);

  const resolveNotificationRoute = useCallback(async (notification) => {
    const metadata = notification?.Metadata || {};

    if (typeof metadata?.routePath === "string" && metadata.routePath.startsWith("/")) {
      const isLegacyGenericServicePath = /^\/services\/\d+$/.test(metadata.routePath);
      if (!isLegacyGenericServicePath) {
        return metadata.routePath;
      }
    }

    if (notification?.SourceType === "Service" && notification?.SourceID) {
      if (metadata?.serviceCategory === "Interpretation") return `/interpretations/${notification.SourceID}`;
      if (metadata?.serviceCategory === "Translation") return `/translations/${notification.SourceID}`;
      if (metadata?.serviceCategory === "Other") return `/otherServices/${notification.SourceID}`;

      try {
        const response = await api.get(`/services/lookup/${notification.SourceID}`);
        const routePath = response?.data?.routePath;
        if (typeof routePath === "string" && routePath.startsWith("/")) {
          return routePath;
        }
      } catch (error) {
        return null;
      }

      return null;
    }

    if (notification?.SourceType === "ServiceNote" && metadata?.serviceId) {
      if (metadata?.serviceCategory === "Interpretation") return `/interpretations/${metadata.serviceId}`;
      if (metadata?.serviceCategory === "Translation") return `/translations/${metadata.serviceId}`;

      try {
        const response = await api.get(`/services/lookup/${metadata.serviceId}`);
        const routePath = response?.data?.routePath;
        if (typeof routePath === "string" && routePath.startsWith("/")) {
          return routePath;
        }
      } catch (error) {
        return null;
      }

      return `/otherServices/${metadata.serviceId}`;
    }

    if (notification?.SourceType === "Task") {
      const taskId = metadata?.taskId || notification?.SourceID;
      const metadataEntityType = metadata?.entityType;
      const metadataEntityId = metadata?.entityId;
      const metadataServiceType = String(metadata?.serviceType || "").toLowerCase();

      if (metadataEntityType === "Service" && metadataEntityId) {
        if (metadataServiceType.includes("interpretation")) return `/interpretations/${metadataEntityId}`;
        if (metadataServiceType.includes("translation")) return `/translations/${metadataEntityId}`;
        return `/otherServices/${metadataEntityId}`;
      }

      if (!taskId) return null;

      try {
        const response = await api.get(`/tasks/${taskId}`);
        const task = response?.data;

        if (task?.EntityType === "Service" && task?.EntityID) {
          const serviceType = String(task?.ServiceType || "").toLowerCase();
          if (serviceType.includes("interpretation")) return `/interpretations/${task.EntityID}`;
          if (serviceType.includes("translation")) return `/translations/${task.EntityID}`;
          return `/otherServices/${task.EntityID}`;
        }
      } catch (error) {
        return null;
      }
    }

    return null;
  }, []);

  const handleNotificationItemClick = useCallback(
    async (notification) => {
      try {
        // Reminder Watch notifications open the Interpretations Reminders
        // overlay for the target date carried in the payload. The overlay
        // opens immediately; mark-as-read is fire-and-forget so it never
        // blocks the click.
        if (notification?.Type === "reminder_watch") {
          setOverlayData({
            initialDate: getReminderWatchTargetDate(notification),
          });
          setOverlayName(OVERLAY_NAMES.INTERPRETATION_REMINDERS);
          handleNotificationsClose();
          navigate("/interpretations");

          if (!parseReadFlag(notification?.IsRead)) {
            void markNotificationAsRead(notification.id);
          }
          return;
        }

        const route = await resolveNotificationRoute(notification);
        if (route) {
          let markAsReadPromise = null;
          if (!parseReadFlag(notification?.IsRead)) {
            markAsReadPromise = markNotificationAsRead(notification.id).catch((error) => {
              console.error("Failed to mark notification as read after click:", error);
            });
          }

          handleNotificationsClose();
          navigate(route);

          // Keep this fire-and-forget so opening the route is never blocked.
          void markAsReadPromise;
        }
      } catch (error) {
        console.error("Failed to open notification route:", error);
      }
    },
    [markNotificationAsRead, navigate, resolveNotificationRoute, setOverlayData, setOverlayName]
  );

  useEffect(() => {
    fetchNotifications({ playSoundForNew: true });
    fetchNotificationSoundPreference();
  }, [fetchNotifications, fetchNotificationSoundPreference]);

  useEffect(() => {
    fetchTimesheetStatus({ silent: true });

    if (!user?.id) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchTimesheetStatus({ silent: true });
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchTimesheetStatus, user?.id]);

  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleTimesheetUpdated = ({ userId, action }) => {
      if (Number(userId) !== Number(user.id)) return;

      if (action === "clock-in") {
        setIsClockedIn(true);
      } else if (action === "clock-out") {
        setIsClockedIn(false);
        setActiveTimesheetEntry(null);
      }

      fetchTimesheetStatus({ silent: true });
    };

    socket.on("timesheet:updated", handleTimesheetUpdated);

    return () => {
      socket.off("timesheet:updated", handleTimesheetUpdated);
    };
  }, [socket, user?.id, fetchTimesheetStatus]);

  // Lightweight polling: only fetch the count on interval to keep the badge
  // up-to-date without pulling the full notification list every 30s.
  // The full list is fetched on: initial mount, popover open, focus/visibility
  // change, and WebSocket notification:created events.
  useEffect(() => {
    if (!user?.id) return;

    const intervalId = window.setInterval(() => {
      // Only fetch count — no full list, no sound checks
      api.get("/notifications/count")
        .then((countResponse) => {
          if (isMountedRef.current) {
            setUnreadCount(Number(countResponse?.data?.unreadCount || 0));
          }
        })
        .catch(() => {});
    }, NOTIFICATION_SYNC_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [user?.id]);

  // Full notification list fetch on mount, popover open, and visibility/focus
  useEffect(() => {
    if (!user?.id) return;

    const syncNotificationsFull = () => {
      fetchNotifications({
        playSoundForNew: !notificationPopoverOpen,
        silent: true,
      });
    };

    const handleVisibilityOrFocus = () => {
      syncNotificationsFull();
    };

    // Initial full fetch
    syncNotificationsFull();

    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [user?.id, notificationPopoverOpen, fetchNotifications]);

  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleNotificationCreated = ({ notification }) => {
      if (!notification) return;
      const normalizedNotification = normalizeNotification(notification);
      if (!isVisibleNow(normalizedNotification)) return;

      const notificationId = Number(normalizedNotification.id);
      if (!Number.isNaN(notificationId) && knownNotificationIdsRef.current.has(notificationId)) {
        return;
      }
      
      // Guard against retroactive loads (socket reconnection replays).
      // Use a 90-second window to account for server/client clock skew and
      // any processing delay between notification creation and socket delivery.
      const createdAt = new Date(normalizedNotification.createdAt);
      const now = new Date();
      const ageSeconds = (now.getTime() - createdAt.getTime()) / 1000;
      const isRecentlyCreated = Number.isNaN(ageSeconds) || ageSeconds < 120;

      setNotifications((prev) => {
        const alreadyExists = prev.some((item) => Number(item.id) === notificationId);
        if (alreadyExists) return prev;
        return sortNotificationsLatestFirst([normalizedNotification, ...prev]).slice(0, 50);
      });
      
      if (isRecentlyCreated) {
        // Mark as known so the polling interval doesn't double-show it.
        knownNotificationIdsRef.current.add(notificationId);
        knownNotificationReadStateRef.current.set(notificationId, false);
        showFloatingNotification(normalizedNotification);

        const isBackground =
          document.visibilityState !== "visible" || !document.hasFocus();

        if (isBackground && "Notification" in window && Notification.permission === "granted") {
          try {
            const title = normalizedNotification?.Title || "Notification";
            const message = normalizedNotification?.Message || "";
            const desktopNotification = new Notification(title, {
              body: message,
              tag: `ce-notification-${normalizedNotification?.id || Date.now()}`,
              renotify: true,
            });

            desktopNotification.onclick = () => {
              window.focus();
              navigate("/notifications");
              desktopNotification.close();
            };

            window.setTimeout(() => {
              desktopNotification.close();
            }, 12000);
          } catch (error) {}
        }

        playNotificationSound();
      }
      // If the notification is too old to show a card, do NOT add to knownIds.
      // The polling interval will then discover it as "new" and surface it with
      // a card (as long as it falls within the polling freshness window).
    };

    const handleNotificationCount = ({ userId, unreadCount: newUnreadCount }) => {
      if (Number(userId) !== Number(user.id)) return;
      setUnreadCount(Number(newUnreadCount || 0));
    };

    const handleNotificationUpdated = ({ notification }) => {
      if (!notification) return;
      const normalizedNotification = normalizeNotification(notification);
      const notificationId = Number(normalizedNotification.id);
      if (Number.isNaN(notificationId)) return;

      if (parseReadFlag(normalizedNotification.IsRead)) {
        knownNotificationReadStateRef.current.set(notificationId, true);
      }

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) =>
            Number(item.id) === notificationId
              ? {
                  ...item,
                  ...normalizedNotification,
                  IsRead: parseReadFlag(normalizedNotification.IsRead),
                }
              : item
          )
        )
      );
    };

    const handleNotificationReadAll = ({ userId: eventUserId, readAt }) => {
      if (Number(eventUserId) !== Number(user.id)) return;

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => {
            knownNotificationReadStateRef.current.set(item.id, true);
            return {
              ...item,
              IsRead: true,
              ReadAt: item.ReadAt || readAt || new Date().toISOString(),
            };
          })
        )
      );
      setUnreadCount(0);
    };

    const handleNotificationSoundPreferenceUpdated = (event) => {
      if (!event?.detail) return;
      setNotificationSound(event.detail);
    };

    const handleSocketConnect = () => {
      fetchNotifications({
        playSoundForNew: false,
        silent: true,
      });
    };

    socket.on("notification:created", handleNotificationCreated);
    socket.on("notification:updated", handleNotificationUpdated);
    socket.on("notification:read-all", handleNotificationReadAll);
    socket.on("notification:count", handleNotificationCount);
    socket.on("connect", handleSocketConnect);
    window.addEventListener("notification-sound-preference-updated", handleNotificationSoundPreferenceUpdated);

    return () => {
      socket.off("notification:created", handleNotificationCreated);
      socket.off("notification:updated", handleNotificationUpdated);
      socket.off("notification:read-all", handleNotificationReadAll);
      socket.off("notification:count", handleNotificationCount);
      socket.off("connect", handleSocketConnect);
      window.removeEventListener("notification-sound-preference-updated", handleNotificationSoundPreferenceUpdated);
    };
  }, [socket, user?.id, playNotificationSound, showFloatingNotification, fetchNotifications, navigate]);

  useEffect(() => {
    const timers = floatingNotificationTimersRef.current;

    return () => {
      timers.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timers.clear();
    };
  }, []);

  // Track mounted state to avoid state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const groupedFloatingNotifications = useMemo(() => {
    const grouped = new Map();

    floatingNotifications.forEach((item) => {
      const key = getNotificationGroupKey(item);
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }

      grouped.get(key).push(item);
    });

    return Array.from(grouped.entries()).map(([groupKey, items]) => ({
      groupKey,
      items: sortNotificationsLatestFirst(items),
    }));
  }, [floatingNotifications]);

  // State to handle the sign out process overlay
  const [signingOut, setSigningOut] = useState(false);

  // Determine if we should show the back button
  const showBackButton = useMemo(() => {
    const path = location.pathname;
    // Show back button on detail/profile pages
    return (
      path.startsWith("/interpretations/") ||
      path.startsWith("/translations/") ||
      path.startsWith("/otherServices/") ||
      path.startsWith("/clients/") ||
      path.startsWith("/linguists/") ||
      path.startsWith("/invoices/") ||
      path.startsWith("/members/") ||
      path.startsWith("/bugReports/")
    );
  }, [location.pathname]);

  // Get the back destination based on current path
  const getBackDestination = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith("/interpretations/")) return "/interpretations";
    if (path.startsWith("/translations/")) return "/translations";
    if (path.startsWith("/otherServices/")) return "/otherServices";
    if (path.startsWith("/clients/")) return "/clients";
    if (path.startsWith("/linguists/")) return "/linguists";
    if (path.startsWith("/invoices/")) return "/invoices";
    if (path.startsWith("/members/")) return "/members";
    if (path.startsWith("/bugReports/")) return "/bugReports";
    return "/";
  }, [location.pathname]);

  const handleBackClick = () => {
    // Clear overlay state first
    setOverlayData(null);

    // If page was opened directly → no history inside the app
    if (location.key === "default") {
      navigate(getBackDestination);
    } else {
      navigate(-1);
    }
  };

  // Get page title based on current route
  const pageTitle = useMemo(() => {
    // If a custom page title is set (e.g., from LinguistProfile), use it
    if (customPageTitle) {
      return customPageTitle;
    }

    const path = location.pathname;

    // Handle root paths
    if (path === "/" || path === "/login" || path === "/loginConfirmation") {
      return "";
    }

    // Handle specific routes
    if (path === "/dashboard") return "Dashboard";
    if (path === "/interpretations") return "Interpretations";
    if (path.startsWith("/interpretations/")) return "Interpretation Details";
    if (path === "/translations") return "Translations";
    if (path.startsWith("/translations/")) return "Translation Details";
    if (path === "/otherServices") return "Other Services";
    if (path.startsWith("/otherServices/")) return "Other Service Details";
    if (path === "/clients") return "Clients";
    if (path.startsWith("/clients/")) return "Client Profile";
    if (path === "/linguists") return "Linguists";
    if (path.startsWith("/linguists/")) return "Linguist Profile";
    if (path === "/calendar") return "Calendar";
    if (path === "/tasks") return "Tasks";
    if (path === "/help") return "Help";
    if (path === "/expenses") return "Expenses";
    if (path === "/invoices") return "Invoices";
    if (path.startsWith("/invoices/")) return "Invoice Details";
    if (path === "/invoicetemplate") return "Invoice Template";
    if (path === "/linguistsPayments") return "Linguists Payments";
    if (path === "/reports") return "Reports";
    if (path === "/members") return "Members";
    if (path.startsWith("/members/")) return "Member Profile";
    if (path === "/settings") return "Settings";
    if (path === "/bugReports") return "Bug Reports";
    if (path.startsWith("/bugReports/")) return "Bug Report Details";
    if (path === "/operationsTools") return "Operations Tools";
    if (path === "/404") return "Page Not Found";

    // Default fallback
    return "";
  }, [location.pathname, customPageTitle]);

  // Updated handleLogout to gradually change the overlay color to theme.palette.background.default:
  // 1. Set signingOut state true to trigger backdrop transition.
  // 2. Wait for 3 seconds for the backdrop to transition.
  // 3. Wait an additional 2 seconds.
  // 4. Call doSignOut and then remove the overlay.
  const handleLogout = async () => {
    setSigningOut(true);
    // Wait 3 seconds for the backdrop to transition to theme.palette.background.default

    handleClose();

    await new Promise((resolve) => setTimeout(resolve, 800));
    // Wait an additional 2 seconds while at the target overlay color
    await doSignOut();
    setSigningOut(false);
  };

  // Handle sidebar toggle
  const handleToggleSidebar = () => {
    if (isMobileScreen) {
      if (!activeMenu) {
        setActiveMenu(true);
        setContextIsCollapsed(false);
      } else {
        setActiveMenu(false);
      }
      return;
    }

    if (!activeMenu) {
      // If sidebar is closed, open it fully
      setActiveMenu(true);
      setContextIsCollapsed(false);
    } else if (!contextIsCollapsed) {
      // If sidebar is fully open, collapse it
      setContextIsCollapsed(true);
    } else if (contextIsCollapsed) {
      // If sidebar is collapsed, expand it (not close it)
      setContextIsCollapsed(false);
    }
  };

  return (
    <>
      <AppBar
        sx={{
          position: "static",
          background: "none",
          boxShadow: (theme) => theme.shadows[6],
          height: "100%",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "100%",
            height: "100%",
            py: 0,
            px: { xs: 1, sm: 2 },
            gap: 1,
          }}
        >
          {/* LEFT SIDE - Back Button and Page Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", flex: { xs: 1, md: 1 } }}>
            {!activeMenu && (
              <IconButton
                onClick={handleToggleSidebar}
                sx={{
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <MenuRoundedIcon sx={{ fontSize: 32 }} />
              </IconButton>
            )}
            {showBackButton && (
              <IconButton
                onClick={handleBackClick}
                sx={{
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ChevronLeftRounded sx={{ fontSize: 32 }} />
              </IconButton>
            )}
            {pageTitle && (
              <Typography
                variant="h2"
                fontWeight="bold"
                color={theme.palette.primary.main}
                sx={{
                  display: "block",
                  minWidth: 0,
                  maxWidth: { xs: "46vw", sm: "none" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "1rem", sm: theme.typography.h2.fontSize },
                  lineHeight: { xs: 1.2, sm: theme.typography.h2.lineHeight },
                }}
              >
                {pageTitle}
              </Typography>
            )}
            <Box sx={{ display: { xs: "none", sm: "flex" } }}>
              <PresenceIndicator viewers={viewers} />
            </Box>
          </Box>

          {/* RIGHT SIDE - Desktop */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, ml: "auto" }}>
            <GlobalSearchBar user={user} />

            <IconButton
              aria-label="Quick Create"
              onClick={handleQuickCreateClick}
              sx={{
                color: theme.palette.primary.main,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                boxShadow: theme.shadows[3],
                p: "8px",
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <AddRounded />
            </IconButton>

            <Menu
              open={quickCreateOpen}
              anchorEl={quickCreateAnchorEl}
              onClose={handleQuickCreateClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    width: 240,
                    bgcolor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.divider}`,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.main,
                    },
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.text }}>
                  Quick Create
                </Typography>
              </Box>

              <Divider />

              <MenuItem onClick={handleQuickCreateTaskClick} sx={{ mt: 0.75 }}>
                <ListItemIcon>
                  <FormatListBulletedRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>Task</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleQuickCreateInterpretationClick}>
                <ListItemIcon>
                  <InterpreterModeRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>Interpretation</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleQuickCreateTranslationClick}>
                <ListItemIcon>
                  <TranslateRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>Translation</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleQuickCreateOtherServiceClick}>
                <ListItemIcon>
                  <MoreHorizRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>Other Service</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleQuickCreateClientClick}>
                <ListItemIcon>
                  <PeopleRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>Client</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleQuickCreateLinguistClick}>
                <ListItemIcon>
                  <RecordVoiceOverRounded fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.primary.text }}>Linguist</ListItemText>
              </MenuItem>

              {isAdmin && <Divider />}

              {isAdmin && (
                <MenuItem onClick={handleQuickCreateExpenseClick}>
                  <ListItemIcon>
                    <PointOfSaleRounded fontSize="small" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: theme.palette.primary.text }}>Expense</ListItemText>
                </MenuItem>
              )}
              {isAdmin && (
                <MenuItem onClick={handleQuickCreateInvoiceClick}>
                  <ListItemIcon>
                    <RequestPageRounded fontSize="small" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: theme.palette.primary.text }}>Invoice</ListItemText>
                </MenuItem>
              )}

              <Divider />

              {Number(user?.id) === 2 && (
                <MenuItem onClick={handleQuickCreateOverlayShowcaseClick}>
                  <ListItemIcon>
                    <DashboardRounded fontSize="small" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: theme.palette.primary.text }}>Overlay Showcase</ListItemText>
                </MenuItem>
              )}
            </Menu>

            <IconButton
              onClick={handleNotificationsClick}
              sx={{
                color: theme.palette.primary.main,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                boxShadow: theme.shadows[3],
                p: "8px",
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                max={99}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiBadge-badge": {
                    height: "20px",
                    minWidth: "20px",
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    px: 0.75,
                    top: -4,
                    right: -4,
                  },
                }}
              >
                <NotificationsNoneRounded />
              </Badge>
            </IconButton>

            <Popover
              open={notificationPopoverOpen}
              anchorEl={notificationAnchorEl}
              onClose={handleNotificationsClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    width: 360,
                    maxHeight: "55vh",
                    borderRadius: 2,
                    overflow: "hidden",
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h5" fontWeight="bold">Notifications</Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button size="small" onClick={markAllNotificationsAsRead} disabled={!unreadCount}>
                    Mark all read
                  </Button>
                  <Button size="small" onClick={() => { handleNotificationsClose(); navigate("/notifications"); }}>
                    Show all
                  </Button>
                </Box>
              </Box>
              <Divider />

              {loadingNotifications ? (
                <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ py: 5, px: 2 }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    No notifications yet.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, maxHeight: "calc(55vh - 60px)", overflowY: "auto" }}>
                  {notifications.map((item) => (
                    <ListItemButton
                      key={item.id}
                      onClick={() => handleNotificationItemClick(item)}
                      sx={{
                        alignItems: "flex-start",
                        bgcolor: parseReadFlag(item.IsRead) ? "transparent" : theme.palette.action.hover,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", gap: 1 }}>
                        <ListItemText
                          primary={item.Title || "Notification"}
                          secondary={
                            <Box component="div" sx={{ display: "block", mt: 0.5 }}>
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block" }}>
                                {item.Message || ""}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                {formatRelativeTime(item.VisibleAt || item.createdAt)}
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{
                            fontWeight: parseReadFlag(item.IsRead) ? 500 : 700,
                          }}
                          secondaryTypographyProps={{
                            component: "div",
                          }}
                        />

                        {!parseReadFlag(item.IsRead) && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              markNotificationAsRead(item.id);
                            }}
                            sx={{
                              mt: 0.25,
                              color: theme.palette.primary.main,
                              border: `1px solid ${theme.palette.primary.border2}`,
                              borderRadius: "10px",
                            }}
                            title="Mark as read"
                            aria-label="Mark as read"
                          >
                            <MarkEmailReadRounded fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Popover>

            {floatingNotifications.length > 0 && (
              <Box
                sx={{
                  position: "fixed",
                  top: `${NAVBAR_HEIGHT + 16}px`,
                  right: 16,
                  width: { xs: "calc(100vw - 32px)", sm: 360 },
                  maxWidth: "calc(100vw - 32px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  zIndex: (theme) => theme.zIndex.snackbar,
                  pointerEvents: "none",
                }}
              >
                {groupedFloatingNotifications.map(({ groupKey, items }) => {
                  const firstItem = items[0];

                  return (
                    <Card
                      key={groupKey}
                      sx={{
                        pointerEvents: "auto",
                        borderRadius: "16px",
                        border: `1px solid ${theme.palette.primary.border2}`,
                        boxShadow: theme.shadows[8],
                        backgroundColor: theme.palette.background.paper,
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, justifyContent: "space-between" }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" sx={{ display: "block", color: theme.palette.primary.main, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
                              {items.length > 1
                                ? `${items.length} notifications`
                                : firstItem?.Type === "INTERPRETATION_START_REMINDER"
                                ? "Interpretation reminder"
                                : firstItem?.Type === "SERVICE_DEADLINE_REMINDER" || firstItem?.Type === "SERVICE_DEADLINE_TODAY" || firstItem?.Type === "SERVICE_DEADLINE_PASSED"
                                ? "Deadline reminder"
                                : firstItem?.Title || "Notification"}
                            </Typography>

                            <Stack spacing={0.75}>
                              {items.map((item) => {
                                const serviceName = item?.Metadata?.serviceName || item?.Title || "Notification";
                                const notificationSummary = item?.Metadata?.notificationSummary || item?.Message || "";

                                return (
                                  <Box
                                    key={item.id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleNotificationItemClick(item);
                                      dismissFloatingNotification(item.id);
                                    }}
                                    sx={{
                                      borderRadius: "10px",
                                      px: 0.5,
                                      py: 0.5,
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: theme.palette.action.hover,
                                      },
                                    }}
                                  >
                                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: "break-word" }}>
                                      {serviceName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ display: "block", wordBreak: "break-word" }}>
                                      {notificationSummary}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>

                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              {formatRelativeTime(firstItem?.VisibleAt || firstItem?.createdAt)}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              dismissFloatingNotificationGroup(items);
                            }}
                            sx={{ mt: -0.25, mr: -0.25, color: theme.palette.text.secondary }}
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )}

          </Box>

          {/* RIGHT SIDE - Mobile (3-dot menu) */}
          <IconButton
            onClick={(event) => setMobileMenuAnchorEl(event.currentTarget)}
            sx={{
              display: { xs: "flex", md: "none" },
              color: theme.palette.primary.main,
              borderRadius: "12px",
              bgcolor: theme.palette.background.default,
              boxShadow: theme.shadows[3],
              p: "8px",
              ml: "auto",
              flexShrink: 0,
              "&:hover": {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <MoreVertRounded />
          </IconButton>

          <Menu
            open={mobileMenuOpen}
            anchorEl={mobileMenuAnchorEl}
            onClose={() => setMobileMenuAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 200,
                  bgcolor: theme.palette.background.alt,
                  border: `1px solid ${theme.palette.divider}`,
                },
              },
            }}
          >
            <MenuItem onClick={() => { setMobileMenuAnchorEl(null); setOverlayName(OVERLAY_NAMES.GLOBAL_SEARCH); }}>
              <SearchRounded sx={{ mr: 1 }} />
              <Typography>Search</Typography>
            </MenuItem>
            <MenuItem onClick={() => { setMobileMenuAnchorEl(null); handleQuickCreateClick({ currentTarget: mobileMenuAnchorEl }); }}>
              <AddRounded sx={{ mr: 1 }} />
              <Typography>Quick Create</Typography>
            </MenuItem>
            <MenuItem onClick={() => { setMobileMenuAnchorEl(null); handleNotificationsClick({ currentTarget: mobileMenuAnchorEl }); }}>
              <NotificationsNoneRounded sx={{ mr: 1 }} />
              <Typography>Notifications {unreadCount > 0 ? `(${unreadCount})` : ""}</Typography>
            </MenuItem>
          </Menu>

          {/* User Profile */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: { xs: 0, md: 1.75 },
            }}
          >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
              <IconButton onClick={handleClick} sx={{ p: 0 }}>
                <Avatar
                  sx={{
                    bgcolor: getAvatarColor(user?.Name),
                    width: { xs: 34, sm: 40 },
                    height: { xs: 34, sm: 40 },
                    borderRadius: "12px",
                    boxShadow: showClockPresenceStyle
                      ? `0 0 0 2px ${presenceGlowColor}, 0 0 12px ${presenceGlowColor}`
                      : theme.shadows[3],
                    animation: showClockPresenceStyle && !isClockedIn
                      ? "ceClockOutPulseScale 1.8s ease-in-out infinite"
                      : "none",
                    transformOrigin: "center center",
                    "@keyframes ceClockOutPulseScale": {
                      "0%": { transform: "scale(1)", opacity: 1 },
                      "50%": { transform: "scale(1.03)", opacity: 0.9 },
                      "100%": { transform: "scale(1)", opacity: 1 },
                    },
                  }}
                  src={user?.ImageThumbnail}
                >
                  {user && user.Name ? user.Name.charAt(0) : ""}
                </Avatar>
              </IconButton>
              {showClockPresenceStyle && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -1,
                    right: -1,
                    width: 11,
                    height: 11,
                    borderRadius: "50%",
                    border: `2px solid ${presenceGlowColor}`,
                    backgroundColor: presenceGlowColor,
                    boxShadow: `0 0 10px ${presenceGlowColor}`,
                    animation: isClockedIn ? "none" : "ceClockOutPulseDotScale 1.8s ease-in-out infinite",
                    transformOrigin: "center center",
                    "@keyframes ceClockOutPulseDotScale": {
                      "0%": { transform: "scale(1)", opacity: 1 },
                      "50%": { transform: "scale(1.15)", opacity: 0.85 },
                      "100%": { transform: "scale(1)", opacity: 1 },
                    },
                  }}
                />
              )}
              </Box>
              <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      minWidth: 260,
                      borderRadius: 2,
                      p: 1.5,
                    },
                  },
                }}
              >
                <FlexBetween alignItems="center" gap={1.25} sx={{ mb: 1.5 }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(user?.Name),
                        width: 36,
                        height: 36,
                        borderRadius: "12px",
                        boxShadow: showClockPresenceStyle
                          ? `0 0 0 2px ${presenceGlowColor}, 0 0 12px ${presenceGlowColor}`
                          : theme.shadows[3],
                        animation: showClockPresenceStyle && !isClockedIn
                          ? "ceClockOutPulseScale 1.8s ease-in-out infinite"
                          : "none",
                      }}
                      src={user?.ImageThumbnail}
                    >
                      {user && user.Name ? user.Name.charAt(0) : ""}
                    </Avatar>
                    {showClockPresenceStyle && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -1,
                          right: -1,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          border: `2px solid ${presenceGlowColor}`,
                          backgroundColor: presenceGlowColor,
                          boxShadow: `0 0 8px ${presenceGlowColor}`,
                          animation: isClockedIn ? "none" : "ceClockOutPulseDotScale 1.8s ease-in-out infinite",
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      {user?.Name || "User"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.Email || user?.email || ""}
                    </Typography>
                  </Box>
                </FlexBetween>

                <Box
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: "10px",
                    backgroundColor: isClockedIn
                      ? theme.palette.success.light
                      : theme.palette.error.light,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={isClockedIn ? "text.primary" : theme.palette.background.default}
                  >
                    {isClockedIn ? "Status: Clocked In" : "Status: Clocked Out"}
                  </Typography>
                  {isClockedIn && activeTimesheetEntry?.ClockInAt ? (
                    <Typography variant="caption" color="text.secondary">
                      Started: {new Date(activeTimesheetEntry.ClockInAt).toLocaleString("en-US")}
                    </Typography>
                  ) : null}
                </Box>

                {!isTimesheetsHiddenForCurrentUser && (
                  <FlexBetween gap={1} sx={{ mb: 1 }}>
                    <Button
                      startIcon={<PlayArrowRounded />}
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={isClockedIn || isClockActionLoading || isTimesheetStatusLoading}
                      onClick={handleClockIn}
                      sx={{
                        justifyContent: "flex-start",
                        px: 1.5,
                      }}
                    >
                      Clock In
                    </Button>

                    <Button
                      startIcon={<StopRounded />}
                      variant="contained"
                      color="error"
                      fullWidth
                      disabled={!isClockedIn || isClockActionLoading || isTimesheetStatusLoading}
                      onClick={handleClockOut}
                      sx={{
                        justifyContent: "flex-start",
                        px: 1.5,
                      }}
                    >
                      Clock Out
                    </Button>
                  </FlexBetween>
                )}

                <Divider sx={{ my: 1 }} />

                <Button
                  startIcon={<AccountCircle />}
                  fullWidth
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    mb: 1,
                    px: 2,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      backgroundColor: theme.palette.primary[100],
                    },
                  })}
                  onClick={() => {
                    handleClose();
                    navigate("/settings");
                  }}
                >
                  Profile
                </Button>

                <Button
                  startIcon={
                    theme.palette.mode === "dark" ? (
                      <LightModeOutlined />
                    ) : (
                      <DarkModeOutlined />
                    )
                  }
                  fullWidth
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    mb: 1,
                    px: 2,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      backgroundColor: theme.palette.primary[100],
                    },
                  })}
                  onClick={() => {
                    dispatch(setMode());
                    handleClose();
                  }}
                >
                  Switch Theme
                </Button>

                <Divider sx={{ my: 1 }} />

                <Button
                  startIcon={<Logout />}
                  color="error"
                  fullWidth
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    px: 2,
                    color: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.error.light,
                      color: theme.palette.background.default,
                    },
                  })}
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </Popover>
            </Box>

        </Toolbar>
      </AppBar>

      {showClockReminderBanner && !isClockedIn && (
        <Box
          sx={{
            width: "100%",
            borderTop: `1px solid ${theme.palette.error.main}`,
            borderBottom: `1px solid ${theme.palette.error.main}`,
            backgroundColor: theme.palette.error.light,
            px: { xs: 1, sm: 2 },
            py: 0.75,
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Typography variant="body2" fontWeight={700} color={theme.palette.background.default}>
            You are active in Portal and currently not clocked in
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleClockReminderClockInNow}
              disabled={isClockActionLoading || isTimesheetStatusLoading || isClockedIn}
              sx={(theme) => ({
                textTransform: "none",
                "&:hover": {
                  backgroundColor: theme.palette.success.dark,
                  color: theme.palette.common.white,
                },
              })}
            >
              Clock In now
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleClockReminderSnooze}
              sx={(theme) => ({
                textTransform: "none",
                borderColor: theme.palette.background.default,
                color: theme.palette.background.default,
                "&:hover": {
                  backgroundColor: theme.palette.error.main,
                  borderColor: theme.palette.common.white,
                  color: theme.palette.common.white,
                },
              })}
            >
              Remind me in 30 min
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={handleClockReminderDismissToday}
              sx={(theme) => ({
                textTransform: "none",
                color: theme.palette.background.default,
                "&:hover": {
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.common.white,
                },
              })}
            >
              Dismiss for today
            </Button>
          </Box>
        </Box>
      )}

      {/* Auto-note reconnect awareness dialog */}
      {pendingAutoNoteEntry && (
        <Dialog
          open
          onClose={handleAutoNoteDismiss}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            You may have forgotten to clock out
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              The Portal was closed while your timesheet was still open. A note was automatically added to your entry:
            </DialogContentText>
            {parseAutoNoteSuggestedTime(pendingAutoNoteEntry.Notes) && (
              <DialogContentText sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", mb: 1 }}>
                Suggested clock-out time: {parseAutoNoteSuggestedTime(pendingAutoNoteEntry.Notes)}
              </DialogContentText>
            )}
            <DialogContentText>
              Were you still working? If so, you can dismiss this. If you actually stopped working around that time, click <strong>Review Entry</strong> to update it.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleAutoNoteDismiss}
              sx={{ textTransform: "none" }}
            >
              I was still working
            </Button>
            <Button
              variant="contained"
              onClick={handleAutoNoteReview}
              sx={{ textTransform: "none" }}
            >
              Review Entry
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Backdrop overlay during sign out with gradual transition to theme.palette.background.default */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.default,
        }}
        open={signingOut}
        transitionDuration={800} // Duration in milliseconds (3 seconds)
      ></Backdrop>
    </>
  );
};

export default Navbar;
